import { Card, Col, Row, Form, FormInstance } from "antd";
import { useCallback, useMemo, useState } from "react";
import { Loader } from "../../../../../../components/Loader";
import { GeneralInfo } from "./components/GeneralInfo";
import { OtherInfo } from "./components/OtherInfo";
import { Impounds } from "./components/Impounds";
import { LateCharges } from "../../../../../Administration/LateChargesTab";
import { PayeeDisbursements } from "./components/PayeeDisbursements";
import { Fees } from "../../../../../Administration/FeeTab";
import { Payers } from "./components/Payers";
import { usePermissions } from "../../../../../../lib/hooks/usePermissions";
import { Banners } from "./components/Banners";
import "./styles.scss";

type DetailsProps = {
  id: string;
  form: FormInstance;
  data: any;
  isLoading: boolean;
  isDisabled: boolean;
  lcSectionKey: boolean;
  setHasChanges: (value: boolean) => void;
  reloadAccount: () => void;
  disbursementsSidebarIsOpen: boolean;
  setDisbursementsSidebarIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  impoundsSidebarIsOpen: boolean;
  setImpoundsSidebarIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  feesSidebarIsOpen: boolean;
  setFeesSidebarIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export const Details = ({
  id,
  form,
  data,
  isLoading,
  isDisabled,
  setHasChanges,
  reloadAccount,
  lcSectionKey,
  disbursementsSidebarIsOpen,
  setDisbursementsSidebarIsOpen,
  impoundsSidebarIsOpen,
  setImpoundsSidebarIsOpen,
  feesSidebarIsOpen,
  setFeesSidebarIsOpen
}: DetailsProps) => {
  const [shouldPayeeDisbUpdate, setShouldPayeeDisbUpdate] = useState<boolean>(false);

  const { hasAccessByController } = usePermissions("Contracts");

  const [lCDays1, setLCDays1] = useState<number>(0);

  const onChange = useCallback((): void => {
    setHasChanges(true);
  }, [setHasChanges]);

  const isPassthru: boolean = useMemo(() => (data ? data.paymentType === 4 : false), [data]);

  const isFormDisabled: boolean = useMemo(
    () => !hasAccessByController("Modify") || isDisabled,
    [hasAccessByController, isDisabled]
  );

  return (
    <Row className="my-6 mx-8 fixed-container justify-center d-flex">
      <Row className="account-details-wrapper">
        {data && <Banners paymentRestrictions={data.paymentRestrictions} notifications={data.bannerNotifications} />}
        <Form
          form={form}
          autoComplete="off"
          layout="vertical"
          requiredMark={false}
          initialValues={data}
          onFieldsChange={onChange}
          disabled={isFormDisabled}
          className="w-100"
        >
          <Row className="w-100">
            <div className="w-100 d-flex general-account-info fixed-container">
              <Col span={16} className="pr-4">
                <Card className="h-100">
                  {isLoading ? (
                    <Loader fontSize="64" />
                  ) : (
                    <GeneralInfo form={form} data={data} isDisabled={isFormDisabled} />
                  )}
                </Card>
              </Col>
              <Col span={8}>
                <Card className="h-100">
                  {isLoading ? (
                    <Loader fontSize="64" />
                  ) : (
                    <OtherInfo data={data} form={form} isDisabled={isFormDisabled} lCDays1={lCDays1} />
                  )}
                </Card>
              </Col>
            </div>
          </Row>
        </Form>
        <Row className="mt-4 w-100">
          <Fees
            accountId={id}
            onSuccess={reloadAccount}
            sidebarIsOpen={feesSidebarIsOpen}
            setSidebarIsOpen={setFeesSidebarIsOpen}
          />
          <LateCharges key={`lc${lcSectionKey}`} accountId={id} setLCDays1={setLCDays1} />
          <Impounds
            accountId={id}
            primaryBuyerNumber={data !== null && data.primaryBuyerNumber}
            primarySellerNumber={data !== null && data.primarySellerNumber}
            setShouldPayeeDisbUpdate={setShouldPayeeDisbUpdate}
            reloadAccount={reloadAccount}
            sidebarIsOpen={impoundsSidebarIsOpen}
            setSidebarIsOpen={setImpoundsSidebarIsOpen}
          />
          <Payers contractId={id} primaryBuyerNumber={data !== null && data.primaryBuyerNumber} />
          <PayeeDisbursements
            accountId={id}
            isPassthru={isPassthru}
            primaryBuyerNumber={data !== null && data.primaryBuyerNumber}
            primarySellerNumber={data !== null && data.primarySellerNumber}
            shouldPayeeDisbUpdate={shouldPayeeDisbUpdate}
            setShouldPayeeDisbUpdate={setShouldPayeeDisbUpdate}
            onSuccess={reloadAccount}
            sidebarIsOpen={disbursementsSidebarIsOpen}
            setSidebarIsOpen={setDisbursementsSidebarIsOpen}
          />
        </Row>
      </Row>
    </Row>
  );
};
